import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Download, Upload } from "lucide-react";
import { Modal } from "../ui/Modal";
import { agentsApi, type AgentListItem, type AgentLookups, type ImportDestinationResult } from "../../api/agentsApi";
import { downloadExport, parseFile, type ParsedFile } from "../../lib/dataSheetTransfer";
import { ScrollShadow } from "../ui/ScrollShadow";
import {
  DESTINATION_COLUMNS,
  columnByKey,
  parseDestinationRow,
  suggestDestinationMapping,
  type DestinationColumnKey,
  type ParsedDestinationRow,
} from "../../lib/destinationTransfer";

type Step = "file" | "map" | "preview" | "running" | "done";

const stepTitle: Record<Step, string> = {
  file: "Import destinations · 1. File",
  map: "Import destinations · 2. Map columns",
  preview: "Import destinations · 3. Preview",
  running: "Importing destinations…",
  done: "Import finished",
};

/** What to do with a row whose name already exists. */
type DuplicateAction = "add" | "ignore" | "replace";
const DUPLICATE_ACTIONS: { value: DuplicateAction; label: string }[] = [
  { value: "add", label: "Add anyway" },
  { value: "ignore", label: "Ignore" },
  { value: "replace", label: "Replace existing" },
];

const PREVIEW_LIMIT = 200;

type PreviewRow = {
  index: number;
  name: string;
  parsed: ParsedDestinationRow;
  /** Existing destination with the same name (case-insensitive), if any. */
  existing: AgentListItem | null;
  /** The same name occurs earlier in the file. */
  duplicateInFile: boolean;
  /** The extension number occurs earlier in the file. */
  duplicateExtensionInFile: boolean;
  /** Another existing destination already uses the row's extension number (its name). */
  extensionTakenBy: string | null;
};

type ResultRow =
  | { index: number; name: string; status: "ok"; result: ImportDestinationResult }
  | { index: number; name: string; status: "ignored" }
  | { index: number; name: string; status: "error"; message: string };

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  border: "2px solid var(--portal-border)",
  borderRadius: 8,
  fontSize: "0.9rem",
  fontFamily: "inherit",
  background: "white",
};
const cellInput: React.CSSProperties = { ...inputStyle, padding: "6px 8px", fontSize: "0.85rem", width: "auto" };

/**
 * Import wizard for destinations (CSV / Excel):
 *  1. file    — pick + parse
 *  2. map     — file column → destination field (Name is required)
 *  3. preview — every row validated; rows whose name already exists get a
 *               per-row choice Add / Ignore / Replace (replace archives
 *               the existing destination and creates the imported one)
 *  4. run     — one import call per row; PBX is enabled through the
 *               regular save path so the PBX login is generated (or the
 *               imported one kept) for every created destination
 */
export function DestinationImportModal({
  open,
  onClose,
  existing,
  onImported,
}: {
  open: boolean;
  onClose: () => void;
  existing: AgentListItem[];
  onImported: () => void;
}) {
  const [step, setStep] = useState<Step>("file");
  const [parsed, setParsed] = useState<ParsedFile | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [mapping, setMapping] = useState<(DestinationColumnKey | null)[]>([]);
  const [actions, setActions] = useState<Record<number, DuplicateAction>>({});
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState<ResultRow[]>([]);
  /** Database names for Status / CLI so files with those spellings import too. */
  const [names, setNames] = useState<AgentLookups>({ statuses: [], cliTypes: [] });

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    agentsApi
      .lookups()
      .then((l) => {
        if (!cancelled) setNames(l);
      })
      .catch(() => {
        /* UI labels and ids still match without the database names */
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const reset = () => {
    setStep("file");
    setParsed(null);
    setFileError(null);
    setMapping([]);
    setActions({});
    setProgress({ done: 0, total: 0 });
    setResults([]);
  };
  const close = () => {
    if (step === "running") return;
    reset();
    onClose();
  };
  const finish = () => {
    reset();
    onClose();
    onImported();
  };

  // ---- derived -------------------------------------------------------------

  const nameMapped = mapping.includes("agentName");

  // Destinations may share a name, so a name maps to all destinations carrying it.
  const existingByName = useMemo(() => {
    const m = new Map<string, AgentListItem[]>();
    for (const a of existing) {
      const key = (a.name ?? a.agentName).trim().toLowerCase();
      const list = m.get(key);
      if (list) list.push(a); else m.set(key, [a]);
    }
    return m;
  }, [existing]);

  // The PBX username is unique, so a row that carries one identifies its destination even when
  // the name was changed or the customer has two destinations with the same name.
  const existingByUsername = useMemo(() => {
    const m = new Map<string, AgentListItem>();
    for (const a of existing) if (a.pbxUsername) m.set(a.pbxUsername.trim().toLowerCase(), a);
    return m;
  }, [existing]);

  const existingByExtension = useMemo(() => {
    const m = new Map<string, AgentListItem>();
    for (const a of existing) if (a.pbxExtension) m.set(a.pbxExtension, a);
    return m;
  }, [existing]);

  const rows = useMemo<PreviewRow[]>(() => {
    if (!parsed) return [];
    const nameCount = new Map<string, number>();
    const nameCol = mapping.indexOf("agentName");
    for (const cells of parsed.rows) {
      const key = nameCol >= 0 ? (cells[nameCol] ?? "").trim().toLowerCase() : "";
      if (key) nameCount.set(key, (nameCount.get(key) ?? 0) + 1);
    }

    const pre = parsed.rows.map((cells, index) => {
      const mapped: Partial<Record<DestinationColumnKey, string>> = {};
      mapping.forEach((key, ci) => {
        if (key) mapped[key] = cells[ci] ?? "";
      });
      const name = (mapped.agentName ?? "").trim();
      const lower = name.toLowerCase();
      return {
        index, mapped, name, lower,
        username: (mapped.pbxUsername ?? "").trim().toLowerCase(),
        number: (mapped.destination ?? "").trim().toLowerCase(),
        ext: (mapped.pbxExtension ?? "").trim(),
        // Same name twice in the file is allowed (destinations may share a name).
        duplicateInFile: lower.length > 0 && (nameCount.get(lower) ?? 0) > 1,
      };
    });

    // Match rows to existing destinations, each destination at most once:
    // 1. PBX username (unique), 2. name when the file and the customer both have it once,
    // 3. a shared name: the same name and number, then whatever is left of that name in file order.
    const matched = new Map<number, AgentListItem>();
    const claimed = new Set<number>();
    const claim = (rowIndex: number, a: AgentListItem) => { matched.set(rowIndex, a); claimed.add(a.agentId); };
    for (const r of pre) {
      const a = r.username ? existingByUsername.get(r.username) : undefined;
      if (a && !claimed.has(a.agentId)) claim(r.index, a);
    }
    const byName = new Map<string, typeof pre>();
    for (const r of pre) {
      if (matched.has(r.index) || !r.lower) continue;
      const list = byName.get(r.lower);
      if (list) list.push(r); else byName.set(r.lower, [r]);
    }
    for (const [lower, group] of byName) {
      const candidates = (existingByName.get(lower) ?? []).filter((a) => !claimed.has(a.agentId));
      if (candidates.length === 0) continue;
      const pending = [...group];
      if (pending.length === 1 && candidates.length === 1) {
        claim(pending[0].index, candidates[0]);
        continue;
      }
      for (const r of [...pending]) {
        const i = candidates.findIndex((a) => !claimed.has(a.agentId) && (a.destination ?? "").trim().toLowerCase() === r.number);
        if (i >= 0) { claim(r.index, candidates[i]); pending.splice(pending.indexOf(r), 1); }
      }
      const rest = candidates.filter((a) => !claimed.has(a.agentId));
      for (const r of pending) {
        const a = rest.shift();
        if (a) claim(r.index, a);
      }
    }

    const seenExt = new Set<string>();
    return pre.map((r) => {
      const p = parseDestinationRow(r.mapped, names);
      const existingRow = matched.get(r.index) ?? null;
      const duplicateExtensionInFile = r.ext.length > 0 && seenExt.has(r.ext);
      if (r.ext) seenExt.add(r.ext);
      // The number may already belong to the destination this row replaces (same destination, or the
      // same PBX login or name in the file); any other owner is a conflict.
      const owner = r.ext ? existingByExtension.get(r.ext) ?? null : null;
      const ownerIsSelf = owner !== null && (
        (existingRow !== null && owner.agentId === existingRow.agentId) ||
        (r.username.length > 0 && owner.pbxUsername.trim().toLowerCase() === r.username) ||
        (r.lower.length > 0 && (owner.name ?? owner.agentName).trim().toLowerCase() === r.lower));
      const extensionTakenBy = owner && !ownerIsSelf ? owner.agentName : null;
      return { index: r.index, name: r.name, parsed: p, existing: existingRow, duplicateInFile: r.duplicateInFile, duplicateExtensionInFile, extensionTakenBy };
    });
  }, [parsed, mapping, existingByName, existingByUsername, existingByExtension, names]);

  const actionFor = (r: PreviewRow): DuplicateAction => actions[r.index] ?? "ignore";
  const duplicates = rows.filter((r) => r.existing);
  const blocked = rows.filter((r) => r.parsed.errors.length > 0 || r.duplicateExtensionInFile || r.extensionTakenBy !== null);
  const toImport = rows.filter((r) => !(r.existing && actionFor(r) === "ignore"));
  const canImport = rows.length > 0 && blocked.length === 0 && toImport.length > 0;

  // ---- actions -------------------------------------------------------------

  async function onFile(file: File | null) {
    if (!file) return;
    setFileError(null);
    try {
      const p = await parseFile(file);
      if (p.headers.length === 0) {
        setFileError("The file has no header row.");
        return;
      }
      setParsed(p);
      setMapping(suggestDestinationMapping(p.headers));
      setActions({});
    } catch (err) {
      setFileError(err instanceof Error ? err.message : "Could not read the file");
    }
  }

  const setAllDuplicates = (a: DuplicateAction) =>
    setActions(Object.fromEntries(duplicates.map((r) => [r.index, a])));

  async function run() {
    setStep("running");
    const out: ResultRow[] = [];
    const work = rows.filter((r) => r.parsed.body);
    setProgress({ done: 0, total: toImport.length });
    for (const r of work) {
      if (r.existing && actionFor(r) === "ignore") {
        out.push({ index: r.index, name: r.name, status: "ignored" });
        continue;
      }
      try {
        const result = await agentsApi.importOne({
          ...r.parsed.body!,
          replaceAgentId: r.existing && actionFor(r) === "replace" ? r.existing.agentId : null,
        });
        out.push({ index: r.index, name: r.name, status: "ok", result });
      } catch (err) {
        out.push({ index: r.index, name: r.name, status: "error", message: err instanceof Error ? err.message : "Import failed" });
      }
      setProgress((p) => ({ ...p, done: p.done + 1 }));
    }
    setResults(out);
    setStep("done");
  }

  const downloadResults = () => {
    const headers = ["Row", "Name", "Result", "Reference", "Agent id", "PBX enabled", "PBX username", "PBX password", "Replaced agent id", "Warnings"];
    const data = results.map((r) =>
      r.status === "ok"
        ? [String(r.index + 2), r.name, r.result.replacedAgentId ? "Replaced" : "Created", r.result.agentRef, String(r.result.agentId), r.result.pbxEnabled ? "Yes" : "No", r.result.pbxUsername, r.result.pbxPassword, r.result.replacedAgentId ? String(r.result.replacedAgentId) : "", r.result.warnings.join(" | ")]
        : [String(r.index + 2), r.name, r.status === "ignored" ? "Ignored" : "Failed", "", "", "", "", "", "", r.status === "error" ? r.message : ""],
    );
    downloadExport("xlsx", "destinations-import-results", headers, data);
  };

  // ---- render ---------------------------------------------------------------

  return (
    <Modal open={open} onClose={close} title={stepTitle[step]} busy={step === "running"} maxWidth={980} footer={renderFooter()}>
      {step === "file" && renderFile()}
      {step === "map" && renderMap()}
      {step === "preview" && renderPreview()}
      {step === "running" && renderRunning()}
      {step === "done" && renderDone()}
    </Modal>
  );

  function renderFooter() {
    const back = (to: Step) => (
      <button type="button" className="btn btn-secondary" onClick={() => setStep(to)}>
        Back
      </button>
    );
    switch (step) {
      case "file":
        return (
          <>
            <button type="button" className="btn btn-secondary" onClick={close}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={() => setStep("map")} disabled={!parsed}>
              Next
            </button>
          </>
        );
      case "map":
        return (
          <>
            {back("file")}
            <button type="button" className="btn btn-primary" onClick={() => setStep("preview")} disabled={!nameMapped}>
              Next
            </button>
          </>
        );
      case "preview":
        return (
          <>
            {back("map")}
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void run()}
              disabled={!canImport}
              title={blocked.length > 0 ? "Fix the highlighted rows first" : undefined}
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <Upload size={16} /> Import {toImport.length} {toImport.length === 1 ? "destination" : "destinations"}
            </button>
          </>
        );
      case "running":
        return null;
      case "done":
        return (
          <>
            <button type="button" className="btn btn-secondary" onClick={downloadResults} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Download size={16} /> Download results
            </button>
            <button type="button" className="btn btn-primary" onClick={finish}>
              Close
            </button>
          </>
        );
    }
  }

  function renderFile() {
    return (
      <div style={{ display: "grid", gap: 12 }}>
        <p style={{ margin: 0, color: "var(--portal-muted)", fontSize: "0.9rem" }}>
          Pick a CSV or Excel file with one destination per row and a header row. A file made with "Export
          destinations" maps automatically; other headers can be mapped in the next step. Only <strong>Name</strong> is
          required; unmapped or empty columns keep the defaults of a new destination.
        </p>
        <input
          type="file"
          accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={(e) => void onFile(e.target.files?.[0] ?? null)}
        />
        {fileError && <Alert>{fileError}</Alert>}
        {parsed && (
          <div style={{ fontSize: "0.9rem" }}>
            <strong>{parsed.fileName}</strong> · {parsed.rows.length} {parsed.rows.length === 1 ? "row" : "rows"} · {parsed.headers.length} columns
          </div>
        )}
      </div>
    );
  }

  function renderMap() {
    if (!parsed) return null;
    const importable = DESTINATION_COLUMNS.filter((c) => c.importable);
    const usedElsewhere = (key: DestinationColumnKey, ci: number) => mapping.some((m, i) => m === key && i !== ci);
    return (
      <div style={{ display: "grid", gap: 12 }}>
        <p style={{ margin: 0, color: "var(--portal-muted)", fontSize: "0.9rem" }}>
          Choose which destination field each file column fills. Yes/No columns accept Yes, No, 1, 0, true, false;
          Status and Phone display accept the name or the id.
        </p>
        {!nameMapped && <Alert>Map a column to <strong>Name</strong> to continue.</Alert>}
        <ScrollShadow style={{ border: "1px solid var(--portal-border)", borderRadius: 8 }} scrollStyle={{ maxHeight: 420, overflowY: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", fontSize: "0.85rem" }}>
            <thead>
              <tr>
                <th style={thStyle}>File column</th>
                <th style={thStyle}>Example</th>
                <th style={thStyle}>Destination field</th>
              </tr>
            </thead>
            <tbody>
              {parsed.headers.map((h, ci) => (
                <tr key={ci}>
                  <td style={tdStyle}>{h || <em>(column {ci + 1})</em>}</td>
                  <td style={{ ...tdStyle, color: "var(--portal-muted)" }}>{parsed.rows[0]?.[ci] ?? ""}</td>
                  <td style={tdStyle}>
                    <select
                      className="portal-input-inline"
                      style={cellInput}
                      value={mapping[ci] ?? ""}
                      onChange={(e) => {
                        const next = [...mapping];
                        next[ci] = (e.target.value || null) as DestinationColumnKey | null;
                        setMapping(next);
                      }}
                    >
                      <option value="">— Skip —</option>
                      {importable.map((c) => (
                        <option key={c.key} value={c.key} disabled={usedElsewhere(c.key, ci)}>
                          {c.label}{c.hint ? ` (${c.hint})` : ""}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollShadow>
      </div>
    );
  }

  function renderPreview() {
    const cols = mapping.filter((k): k is DestinationColumnKey => k != null);
    const shown = rows.slice(0, PREVIEW_LIMIT);
    return (
      <div style={{ display: "grid", gap: 10 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", fontSize: "0.9rem" }}>
          <span>
            <strong>{rows.length}</strong> rows · <strong>{duplicates.length}</strong> already exist · <strong>{blocked.length}</strong> with errors
          </span>
          {duplicates.length > 0 && (
            <label style={{ display: "inline-flex", alignItems: "center", gap: 6, marginLeft: "auto" }}>
              Existing names:
              <select className="portal-input-inline" style={cellInput} defaultValue="" onChange={(e) => e.target.value && setAllDuplicates(e.target.value as DuplicateAction)}>
                <option value="">Set all to…</option>
                {DUPLICATE_ACTIONS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
            </label>
          )}
        </div>
        {duplicates.length > 0 && (
          <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--portal-muted)" }}>
            <strong>Add anyway</strong> creates a second destination with the same name. <strong>Ignore</strong> skips the row.
            <strong> Replace existing</strong> archives the current destination and creates the imported one (new id and, when PBX
            is enabled, a new PBX login unless the file supplies one).
          </p>
        )}
        {blocked.length > 0 && <Alert>{blocked.length} {blocked.length === 1 ? "row has" : "rows have"} errors; fix the file or unmap the column and try again.</Alert>}
        <ScrollShadow style={{ border: "1px solid var(--portal-border)", borderRadius: 8 }} scrollStyle={{ maxHeight: 420, overflowY: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", fontSize: "0.85rem" }}>
            <thead>
              <tr>
                <th style={thStyle}>#</th>
                {cols.map((k) => <th key={k} style={thStyle}>{columnByKey(k).label}</th>)}
                <th style={thStyle}>Status</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => {
                const extTaken = r.extensionTakenBy !== null;
                const bad = r.parsed.errors.length > 0 || r.duplicateExtensionInFile || extTaken;
                const cells = parsed!.rows[r.index];
                return (
                  <tr key={r.index} style={bad ? { background: "#fdecea" } : r.existing ? { background: "#fff8e6" } : undefined}>
                    <td style={tdStyle}>{r.index + 2}</td>
                    {mapping.map((k, ci) => (k ? <td key={ci} style={tdStyle}>{cells[ci]}</td> : null))}
                    <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                      {r.duplicateExtensionInFile ? (
                        <Chip tone="danger">Duplicate extension number in file</Chip>
                      ) : extTaken ? (
                        <span style={{ color: "#c0392b", whiteSpace: "normal" }} title={`Extension number ${r.parsed.body?.pbxExtension ?? ""} is already used by "${r.extensionTakenBy}" in this customer`}>
                          Extension {r.parsed.body?.pbxExtension ?? ""} already used by "{r.extensionTakenBy}"
                        </span>
                      ) : r.parsed.errors.length > 0 ? (
                        <span style={{ color: "#c0392b" }}>{r.parsed.errors.join("; ")}</span>
                      ) : r.existing ? (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          <Chip tone="warn">Already exists</Chip>
                          <select className="portal-input-inline" style={cellInput} value={actionFor(r)} onChange={(e) => setActions({ ...actions, [r.index]: e.target.value as DuplicateAction })}>
                            {DUPLICATE_ACTIONS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                          </select>
                        </span>
                      ) : (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          <Chip tone="ok">New</Chip>
                          {r.duplicateInFile && <span title="Another row in the file has the same name; both are imported as separate destinations" style={{ color: "var(--portal-text-muted, #777)" }}>same name as another row</span>}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </ScrollShadow>
        {rows.length > PREVIEW_LIMIT && (
          <div style={{ fontSize: "0.85rem", color: "var(--portal-muted)" }}>Showing the first {PREVIEW_LIMIT} of {rows.length} rows; all rows are validated.</div>
        )}
      </div>
    );
  }

  function renderRunning() {
    const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;
    return (
      <div style={{ display: "grid", gap: 10 }}>
        <div style={{ fontSize: "0.9rem" }}>{progress.done} of {progress.total} destinations…</div>
        <div style={{ height: 8, background: "var(--portal-surface)", borderRadius: 999, overflow: "hidden" }}>
          <div style={{ width: `${pct}%`, height: "100%", background: "var(--portal-primary)", transition: "width 0.15s" }} />
        </div>
      </div>
    );
  }

  function renderDone() {
    const created = results.filter((r) => r.status === "ok" && !r.result.replacedAgentId).length;
    const replaced = results.filter((r) => r.status === "ok" && r.result.replacedAgentId).length;
    const ignored = results.filter((r) => r.status === "ignored").length;
    const failed = results.filter((r) => r.status === "error").length;
    const warned = results.filter((r) => r.status === "ok" && r.result.warnings.length > 0).length;
    return (
      <div style={{ display: "grid", gap: 10 }}>
        <div style={{ fontSize: "0.95rem" }}>
          <strong>{created}</strong> created · <strong>{replaced}</strong> replaced · <strong>{ignored}</strong> ignored ·{" "}
          <strong style={failed ? { color: "#c0392b" } : undefined}>{failed}</strong> failed
          {warned > 0 && <> · <strong style={{ color: "#8a5a00" }}>{warned}</strong> with warnings</>}
        </div>
        <ScrollShadow style={{ border: "1px solid var(--portal-border)", borderRadius: 8 }} scrollStyle={{ maxHeight: 400, overflowY: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", fontSize: "0.85rem" }}>
            <thead>
              <tr>
                <th style={thStyle}>#</th>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Result</th>
                <th style={thStyle}>Reference</th>
                <th style={thStyle}>PBX username</th>
                <th style={thStyle}>PBX password</th>
                <th style={thStyle}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r) => (
                <tr key={r.index} style={r.status === "error" ? { background: "#fdecea" } : undefined}>
                  <td style={tdStyle}>{r.index + 2}</td>
                  <td style={tdStyle}>{r.name}</td>
                  <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                    {r.status === "ok" ? (
                      <Chip tone="ok">{r.result.replacedAgentId ? `Replaced (was ${r.result.replacedAgentId})` : "Created"}</Chip>
                    ) : r.status === "ignored" ? (
                      <Chip tone="warn">Ignored</Chip>
                    ) : (
                      <Chip tone="danger">Failed</Chip>
                    )}
                  </td>
                  <td style={tdStyle}>{r.status === "ok" ? r.result.agentRef : ""}</td>
                  <td style={{ ...tdStyle, fontFamily: "inherit" }}>{r.status === "ok" && r.result.pbxEnabled ? r.result.pbxUsername : ""}</td>
                  <td style={{ ...tdStyle, fontFamily: "inherit" }}>{r.status === "ok" && r.result.pbxEnabled ? r.result.pbxPassword : ""}</td>
                  <td style={{ ...tdStyle, color: "#8a5a00" }}>
                    {r.status === "ok" ? r.result.warnings.join(" · ") : r.status === "error" ? <span style={{ color: "#c0392b" }}>{r.message}</span> : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollShadow>
      </div>
    );
  }
}

// ---- small parts ---------------------------------------------------------

const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "8px 10px",
  borderBottom: "1px solid var(--portal-border)",
  color: "var(--portal-muted)",
  fontSize: "0.75rem",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  position: "sticky",
  top: 0,
  background: "white",
  whiteSpace: "nowrap",
};
const tdStyle: React.CSSProperties = {
  padding: "6px 10px",
  borderBottom: "1px solid var(--portal-border)",
  verticalAlign: "top",
  maxWidth: 240,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

function Chip({ tone, children }: { tone: "ok" | "warn" | "danger"; children: React.ReactNode }) {
  const colors = {
    ok: { color: "#1e7e34", background: "#e6f4ea", border: "#a3d9b1" },
    warn: { color: "#8a5a00", background: "#fff4e0", border: "#f0c36d" },
    danger: { color: "#c0392b", background: "#fdecea", border: "#e74c3c" },
  }[tone];
  return (
    <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 999, fontSize: "0.75rem", fontWeight: 600, color: colors.color, background: colors.background, border: `1px solid ${colors.border}` }}>
      {children}
    </span>
  );
}

function Alert({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "flex-start", color: "#c0392b", background: "#fdecea", border: "1px solid #e74c3c", borderRadius: 8, padding: 10, fontSize: "0.9rem" }}>
      <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
      <div>{children}</div>
    </div>
  );
}
